import {Component, Inject, OnInit} from '@angular/core';
import {FormBuilder} from '@angular/forms';
import {LoadingSpinnerService} from '@harmony-modules/configuration/sections/employee/employee-import/shared/loading-spinner/loading-spinner.service';
import {SystemPreferenceService} from '@harmony-modules/payroll/services/admin-setup/system-preference.service';
import {TRANSLOCO_SCOPE, TranslocoService} from '@ngneat/transloco';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {WizardService} from '@services/wizard.service';
import {scopeLoader} from '@shared/i18n/transloco-scoped-loader.helper.';
import {switchMap, tap} from 'rxjs/operators';
import {AuthenticationService} from 'src/app/shared/services';
import {BackupService} from 'src/app/shared/services/backup.service';
import {IBackup, IBackupDir} from '@shared/models/ibackup';
import {AccessControlService} from '../../../account/access-control/services/access-control.service';
import {SecurityKeys} from '@shared/models/ISecurity';

declare var $: any;
declare var showMsg: any;

@UntilDestroy()
@Component({
  selector: 'app-backup',
  templateUrl: './backup.component.html',
  styleUrls: ['./backup.component.scss'],
  providers: [{
    provide: TRANSLOCO_SCOPE,
    useValue: {scope: 'backup', loader: scopeLoader((lang, root) => import(`./${root}/${lang}.json`))}
  }]
})
export class BackupComponent implements OnInit {
  infoForBackup: IBackup = {a: '', b: '', c: true};
  regex: any;
  lblUsedDBSpace = '';
  lblUsedPaystubSpace = '';
  lblFreeSpace = '';

  donut: any = {
    labels: [this.lblUsedDBSpace, this.lblUsedPaystubSpace, this.lblFreeSpace],
    datasets: [
      {
        data: [],
        backgroundColor: [
          '#556ee6', '#b7997d', '#9bc4e8'
        ],
        hoverBackgroundColor: ['#556ee6', '#b7997d', '#9bc4e8'],
        hoverBorderColor: '#fff'
      }],
    options: {
      maintainAspectRatio: false,
      legend: {
        position: 'top'
      }
    }
  };

  fSize: string;
  userBackupDirs: IBackupDir[] = [];
  sysPreference: any;
  isSupervisor: boolean;
  freeSpace: number;
  companyName: string;
  backupPermission = true;
  backupMessage: { Percent: number, status: number, fileName: string };

  constructor(
    private fb: FormBuilder,
    private _backupService: BackupService,
    private _sysPrefService: SystemPreferenceService,
    private authenticationService: AuthenticationService,
    private loading: LoadingSpinnerService,
    private accessControlService: AccessControlService,
    public wizardService: WizardService,
    @Inject(TRANSLOCO_SCOPE) private scope,
    private transloco: TranslocoService
  ) {
    this.authenticationService.currentUser.subscribe(x => this.companyName = x.selectedCompany.Name);
  }

  ngOnInit() {
    this.backupPermission = this.accessControlService.getAccessPermissions('core', SecurityKeys.coreModule.systemTools.companyBackup, 'view') as boolean;
    this._backupService.updateBackupStatus(false);
    this.transloco.selectTranslate(null, {}, this.scope).pipe(
      untilDestroyed(this),
      tap(() => {
        this.lblUsedDBSpace = this.transloco.translate('usedDBSpace', {}, 'backup');
        this.lblUsedPaystubSpace = this.transloco.translate('usedPaystubSpace', {}, 'backup');
        this.lblFreeSpace = this.transloco.translate('freeSpace', {}, 'backup');
        this.donut.labels = [this.lblUsedDBSpace, this.lblUsedPaystubSpace, this.lblFreeSpace];
      }),
      switchMap(() => this._backupService.getUserSpace())
    ).subscribe(
      d => {
        this.freeSpace = d.freeSpace;
        this.fSize = d.freeSize;
        this.donut.labels = [`${this.lblUsedDBSpace} (${d.usedDBSize})`, `${this.lblUsedPaystubSpace} (${d.usedPaystubSize})`, `${this.lblFreeSpace} (${d.freeSize})`];
        this.donut.datasets[0].data = [d.usedDBSpace, d.usedPaystubSpace, d.freeSpace];
      });

    this._backupService.isSupervisor().subscribe(data => {
      this.isSupervisor = data;
    });

    this.regex = '[^</*?"\\\\>:|.]+';
    this._backupService.getUserBackupFiles().subscribe(
      x => {
        this.userBackupDirs = x;
        this.userBackupDirs.forEach(y => {
          y['id'] = y.dirName.replace(' ', '');
        });
      });

    this._sysPrefService.getinitSystemPreferences().subscribe(data => {
      if (data && data.length > 0) {
        this.sysPreference = data[0];
        //  this.companyName = this.sysPreference.j;
      }
    });
  }

  onSubmit(): void {
    this.backupMessage = undefined;
    if (this.freeSpace == 0) {
      const title = this.transloco.translate('error');
      const message = this.transloco.translate('alerts.noFreeSpace', {}, 'backup');
      const button = this.transloco.translate('ok');
      showMsg({
        type: 'sticky', status: 'error', title, message, buttons: {
          [button]: {
            action: () => {
            }
          }
        }
      });
    } else {
      const comp = this.userBackupDirs.find((val) => {
        return val.folder == this.companyName;
      });
      if (comp && comp.files && comp.files.find((val) => {
        return val.name == this.infoForBackup.b.trim().replace('.pym', '');
      })) {
        const that = this;
        const title = this.transloco.translate('warning');
        const msg = this.transloco.translate('alerts.overwriteTheBackupFile', {}, 'backup');
        const button1 = this.transloco.translate('yes');
        const button2 = this.transloco.translate('no');
        showMsg({
          type: 'sticky',
          status: 'warning',
          title,
          buttons: {
            [button1]: {
              action: () => {
                that.loading.startSpinner({spinnerName: 'bkSpinner', isFullScreen: true});
                that._backupService.companyBackup(that.infoForBackup).subscribe(
                  suc => {
                    that.backupMessage = suc;
                    if (suc == -1) {
                      const title = that.transloco.translate('error');
                      const msg1 = that.transloco.translate('alerts.backupFileSizeIsMoreThan', {}, 'backup');
                      const msg2 = that.transloco.translate('alerts.manageBackups', {}, 'backup');
                      const button = that.transloco.translate('ok');
                      showMsg({
                        type: 'sticky', status: 'error', title, message: msg1 + that.fSize + msg2,
                        buttons: {
                          [button]: {
                            action: () => {
                            }
                          }
                        }
                      });
                    } else {
                      if (suc == -66) {
                        const title = that.transloco.translate('error');
                        const message = that.transloco.translate('alerts.backupFolderOnServerIsNotExist', {}, 'backup');
                        showMsg({type: 'nonsticky', status: 'error', title, buttons: {}, message, delay: '1800'});
                      } else {
                        that._backupService.updateBackupStatus(true);
                      }
                    }
                    that.transloco.selectTranslate(null, {}, that.scope).pipe(
                      untilDestroyed(that),
                      tap(() => {
                        this.lblUsedDBSpace = this.transloco.translate('usedDBSpace', {}, 'backup');
                        this.lblUsedPaystubSpace = this.transloco.translate('usedPaystubSpace', {}, 'backup');
                        that.lblFreeSpace = that.transloco.translate('freeSpace', {}, 'backup');
                        that.donut.labels = [this.lblUsedDBSpace, this.lblUsedPaystubSpace, that.lblFreeSpace];
                      }),
                      switchMap(() => that._backupService.getUserSpace())
                    ).subscribe(
                      d => {
                        that.fSize = d.freeSize;
                        that.freeSpace = d.freeSpace;
                        that.donut.labels = [`${that.lblUsedDBSpace} (${d.usedDBSize})`, `${that.lblUsedPaystubSpace} (${d.usedPaystubSize})`, `${that.lblFreeSpace} (${d.freeSize})`];
                        that.donut.datasets[0].data = [d.usedDBSpace, d.usedPaystubSpace, d.freeSpace];
                      });
                    that._backupService.getUserBackupFiles().subscribe(
                      x => {
                        that.userBackupDirs = x;
                        that.userBackupDirs.forEach(y => {
                          y['id'] = y.dirName.replace(' ', '');
                        });
                      });
                    that.loading.stopSpinner();
                  },
                  () => {
                    const title = that.transloco.translate('error');
                    const message = that.transloco.translate('alerts.errorOccurredWhenBackupingTheCompany', {}, 'backup');
                    showMsg({type: 'nonsticky', status: 'error', title, buttons: {}, message, delay: '1800'});
                    that.loading.stopSpinner();
                  }
                );
              }
            },
            [button2]: {
              action: function () {
              }
            }
          },
          'message': msg + that.infoForBackup.b.trim() + ' ?'
        });
      } else {
        this.loading.startSpinner({spinnerName: 'pageSpinner', isFullScreen: true});
        this._backupService.companyBackup(this.infoForBackup).subscribe(
          suc => {
            let title: string;
            this.backupMessage = suc;
            if (suc == -1) {
              title = this.transloco.translate('error');
              const msg1 = this.transloco.translate('alerts.backupFileSizeIsMoreThan', {}, 'backup');
              const msg2 = this.transloco.translate('alerts.manageBackups', {}, 'backup');
              const button = this.transloco.translate('ok');
              showMsg({
                type: 'sticky', status: 'error', title, buttons: {
                  [button]: {
                    action: () => {
                    }
                  }
                }, 'message': msg1 + this.fSize + msg2
              });
            } else {
              if (suc == -66) {
                title = this.transloco.translate('error');
                const message = this.transloco.translate('alerts.backupFolderOnServerIsNotExist', {}, 'backup');
                showMsg({type: 'nonsticky', status: 'error', title, buttons: {}, message, delay: '1800'});
              } else {
                this._backupService.updateBackupStatus(true);
                this.backupMessage = suc;
              }
            }
            this._backupService.getUserBackupFiles().subscribe(
              x => {
                this.userBackupDirs = x;
                this.userBackupDirs.forEach(y => {
                  y['id'] = y.dirName.replace(' ', '');
                });
              });
            this.transloco.selectTranslate(null, {}, this.scope).pipe(
              untilDestroyed(this),
              tap(() => {
                this.lblUsedDBSpace = this.transloco.translate('usedDBSpace', {}, 'backup');
                this.lblUsedPaystubSpace = this.transloco.translate('usedPaystubSpace', {}, 'backup');
                this.lblFreeSpace = this.transloco.translate('freeSpace', {}, 'backup');
                this.donut.labels = [this.lblUsedDBSpace, this.lblUsedPaystubSpace, this.lblFreeSpace];
              }),
              switchMap(() => this._backupService.getUserSpace())
            ).subscribe(
              d => {
                this.freeSpace = d.freeSpace;
                this.fSize = d.freeSize;
                this.donut.labels = [`${this.lblUsedDBSpace} (${d.usedDBSize})`, `${this.lblUsedPaystubSpace} (${d.usedPaystubSize})`, `${this.lblFreeSpace} (${d.freeSize})`];
                this.donut.datasets[0].data = [d.usedDBSpace, d.usedPaystubSpace, d.freeSpace];
              });
            this.loading.stopSpinner();
          },
          () => {
            const title = this.transloco.translate('error');
            const message = this.transloco.translate('alerts.errorOccurredWhenBackupingTheCompany', {}, 'backup');
            showMsg({type: 'nonsticky', status: 'error', title, buttons: {}, message, delay: '1800'});
            this.loading.stopSpinner();
          }
        );
      }
    }
  }

  collapseOnClick(subDir: any) {
    let tabid = '#tab' + subDir;
    let divid = '#div' + subDir;
    $(tabid).toggleClass('active');
    $(divid).toggleClass('showcont');
  }

  fromJsonDate(jDate: Date): string {
    const dateStr = (new Date(jDate)).toISOString().substring(0, 10);
    return dateStr;
  }

  manageBackup() {
    this.transloco.selectTranslate(null, {}, this.scope).pipe(
      untilDestroyed(this),
      tap(() => {
        this.lblUsedDBSpace = this.transloco.translate('usedDBSpace', {}, 'backup');
        this.lblUsedPaystubSpace = this.transloco.translate('usedPaystubSpace', {}, 'backup');
        this.lblFreeSpace = this.transloco.translate('freeSpace', {}, 'backup');
        this.donut.labels = [this.lblUsedDBSpace, this.lblUsedPaystubSpace, this.lblFreeSpace];
      })).subscribe(
      () => this._backupService.getUserBackupFiles().subscribe(
        x => {
          this.userBackupDirs = x;
          $('#fileModal').modal('show');
          this._backupService.getUserSpace().subscribe(
            d => {
              this.freeSpace = d.freeSpace;
              this.fSize = d.freeSize;
              this.donut.labels = [`${this.lblUsedDBSpace} (${d.usedDBSize})`, `${this.lblUsedPaystubSpace} (${d.usedPaystubSize})`, `${this.lblFreeSpace} (${d.freeSize})`];
              this.donut.datasets[0].data = [d.usedDBSpace, d.usedPaystubSpace, d.freeSpace];
              this.userBackupDirs.forEach(y => {
                y['id'] = y.dirName.replace(' ', '');
              });
            });
        }));
  }

  deleteBackup(dirName: string, subDir: string, file: string) {
    const that = this;
    const title = this.transloco.translate('warning');
    const msg = this.transloco.translate('alerts.deleteTheBackupFile', {}, 'backup');
    const button1 = this.transloco.translate('yes');
    const button2 = this.transloco.translate('no');
    showMsg({
      type: 'sticky',
      status: 'warning',
      title,
      buttons: {
        [button1]: {
          action: () => {
            that._backupService.deleteBackupFile(subDir + '\\' + file).subscribe(
              d => {
                if (d == 0) {
                  that._backupService.getUserBackupFiles().subscribe(
                    x => {
                      that.userBackupDirs = x;
                      that.userBackupDirs.forEach(y => {
                        y['id'] = y.dirName.replace(' ', '');
                      });
                    });
                  that.transloco.selectTranslate(null, {}, that.scope).pipe(
                    untilDestroyed(that),
                    tap(() => {
                      that.lblUsedDBSpace = that.transloco.translate('usedDBSpace', {}, 'backup');
                      that.lblUsedPaystubSpace = that.transloco.translate('usedPaystubSpace', {}, 'backup');
                      that.lblFreeSpace = that.transloco.translate('freeSpace', {}, 'backup');
                      that.donut.labels = [that.lblUsedDBSpace, that.lblUsedPaystubSpace, that.lblFreeSpace];
                    }),
                    switchMap(() => that._backupService.getUserSpace())
                  ).subscribe(
                    d => {
                      that.freeSpace = d.freeSpace;
                      that.fSize = d.freeSize;
                      that.donut.labels = [`${that.lblUsedDBSpace} (${d.usedDBSize})`, `${that.lblUsedPaystubSpace} (${d.usedPaystubSize})`, `${that.lblFreeSpace} (${d.freeSize})`];
                      that.donut.datasets[0].data = [d.usedDBSpace, d.usedPaystubSpace, d.freeSpace];
                    });
                  const title = that.transloco.translate('success');
                  const message = that.transloco.translate('alerts.companyBackupFileSuccessfullyDeleted', {}, 'backup');
                  showMsg({type: 'nonsticky', status: 'success', title, buttons: {}, message, delay: '1800'});
                } else {
                  const title = that.transloco.translate('error');
                  const message = that.transloco.translate('alerts.errorOccurredWhenDeletingTheCompanyBackupFile', {}, 'backup');
                  showMsg({type: 'nonsticky', status: 'error', title, buttons: {}, message, delay: '1800'});
                }
              },
              () => {
                const title = that.transloco.translate('error');
                const message = that.transloco.translate('alerts.errorOccurredWhenDeletingTheCompanyBackupFile', {}, 'backup');
                showMsg({type: 'nonsticky', status: 'error', title: title, buttons: {}, message, delay: '1800'});
              }
            );
          }
        },
        [button2]: {
          action: () => {
          }
        }
      },
      message: msg + dirName + ' ?'
    });
  }
}
